import { useEffect, useState } from "react";
import { Award, Crown, Lock, Sparkles, Trophy } from "lucide-react";

import { toast } from "@/components/ui/sonner";
import { fetchAchievementProgress } from "@/lib/api";
import { pickText } from "@/lib/content";

const ICON_MAP = {
  "door-open": "🚪",
  "message-circle": "💬",
  "tree": "🌳",
  "flame": "🔥",
  "wind": "💨",
  "pen": "✒️",
  "messages-square": "📜",
  "eye": "👁️",
  "volume-2": "🔊",
  "users": "👥",
  "crown": "👑",
};

export const AchievementsPage = ({ language, vipEmail }) => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProgress = async () => {
      if (!vipEmail) return;
      try {
        setLoading(true);
        const data = await fetchAchievementProgress(vipEmail);
        setProgress(data);
      } catch (error) {
        console.error(error);
        toast.error(pickText(language, "Unable to load achievements.", "Impossibile caricare gli achievement."));
      } finally {
        setLoading(false);
      }
    };
    loadProgress();
  }, [language, vipEmail]);

  const getProgressPercentage = () => {
    if (!progress) return 0;
    return Math.round((progress.unlocked_count / progress.total_count) * 100);
  };

  return (
    <div className="space-y-10" data-testid="achievements-page">
      <div className="max-w-3xl space-y-4">
        <p className="section-label" data-testid="achievements-eyebrow">
          {pickText(language, "Temple Achievements", "Achievement del Tempio")}
        </p>
        <h2 className="text-5xl text-foreground md:text-6xl" data-testid="achievements-title">
          {pickText(language, "Badges of mastery for temple explorers.", "Distintivi di maestria per gli esploratori del tempio.")}
        </h2>
        <p className="body-copy" data-testid="achievements-description">
          {pickText(language, "Unlock achievements as you explore the Anatomia Occulti, engage with Kōnshus, and participate in the community.", "Sblocca achievement mentre esplori l'Anatomia Occulti, interagisci con Kōnshus e partecipi alla comunità.")}
        </p>
      </div>

      {/* Progress Overview */}
      <div className="glass-card rounded-[2rem] p-6" data-testid="achievements-progress-card">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
              <Trophy className="h-8 w-8 text-primary" />
            </div>
            <div>
              <p className="section-label">{pickText(language, "Your Progress", "I Tuoi Progressi")}</p>
              <p className="mt-1 text-3xl text-foreground" data-testid="achievements-progress-count">
                {loading ? "—" : `${progress?.unlocked_count || 0} / ${progress?.total_count || 0}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-xs uppercase tracking-[0.24em] text-primary/80">{pickText(language, "Total Points", "Punti Totali")}</p>
              <p className="mt-2 text-4xl text-foreground" data-testid="achievements-total-points">
                {loading ? "—" : progress?.total_points || 0}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs uppercase tracking-[0.24em] text-primary/80">{pickText(language, "Completion", "Completamento")}</p>
              <p className="mt-2 text-4xl text-foreground" data-testid="achievements-completion-percent">
                {loading ? "—" : `${getProgressPercentage()}%`}
              </p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="h-3 w-full overflow-hidden rounded-full bg-black/30">
            <div
              className="h-full rounded-full bg-gradient-to-r from-primary/60 to-primary transition-all duration-500"
              data-testid="achievements-progress-bar"
              style={{ width: `${getProgressPercentage()}%` }}
            />
          </div>
        </div>
      </div>

      {/* Unlocked Achievements */}
      {progress?.unlocked?.length > 0 && (
        <div className="space-y-4" data-testid="achievements-unlocked-section">
          <div className="flex items-center gap-3 text-primary">
            <Award className="h-5 w-5" />
            <p className="section-label">{pickText(language, "Unlocked Achievements", "Achievement Sbloccati")}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {progress.unlocked.map((achievement) => (
              <div
                className="glass-card rounded-[1.5rem] border-primary/30 p-5"
                data-testid={`achievement-unlocked-${achievement.achievement_id}`}
                key={achievement.id}
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/20 text-2xl">
                    {ICON_MAP[achievement.icon] || "🏆"}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg text-foreground">
                      {language === "it" ? achievement.title_it : achievement.title_en}
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-slate-400">
                      {language === "it" ? achievement.description_it : achievement.description_en}
                    </p>
                    <div className="mt-3 flex items-center gap-3">
                      <span className="flex items-center gap-1 text-xs text-primary">
                        <Sparkles className="h-3 w-3" />
                        {achievement.points} pts
                      </span>
                      <span className="text-xs text-slate-500">
                        {new Date(achievement.unlocked_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Locked Achievements */}
      {progress?.locked?.length > 0 && (
        <div className="space-y-4" data-testid="achievements-locked-section">
          <div className="flex items-center gap-3 text-slate-500">
            <Lock className="h-5 w-5" />
            <p className="section-label">{pickText(language, "Locked Achievements", "Achievement Bloccati")}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {progress.locked.map((achievement) => (
              <div
                className="glass-card rounded-[1.5rem] border-white/5 p-5 opacity-60"
                data-testid={`achievement-locked-${achievement.id}`}
                key={achievement.id}
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/5 text-2xl grayscale">
                    {ICON_MAP[achievement.icon] || "🔒"}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg text-slate-400">
                      {language === "it" ? achievement.title_it : achievement.title_en}
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-slate-500">
                      {language === "it" ? achievement.description_it : achievement.description_en}
                    </p>
                    <div className="mt-3 flex items-center gap-1 text-xs text-slate-600">
                      <Lock className="h-3 w-3" />
                      {achievement.points} pts
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Temple Master Badge (Special Display) */}
      {progress?.unlocked?.some((a) => a.achievement_id === "temple_master") && (
        <div className="glass-card rounded-[2rem] border-amber-400/30 bg-gradient-to-br from-amber-900/20 to-transparent p-8 text-center" data-testid="achievements-master-badge">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border-2 border-amber-400/50 bg-amber-900/30">
            <Crown className="h-10 w-10 text-amber-400" />
          </div>
          <h3 className="mt-4 text-3xl text-amber-200">{pickText(language, "Temple Master", "Maestro del Tempio")}</h3>
          <p className="mt-2 text-sm text-amber-300/80">
            {pickText(language, "You have unlocked all achievements and proven your mastery of the Anatomia Occulti.", "Hai sbloccato tutti gli achievement e dimostrato la tua maestria dell'Anatomia Occulti.")}
          </p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="rounded-3xl border border-white/10 bg-black/20 p-6 text-center text-sm text-slate-300">
          {pickText(language, "Loading your achievements...", "Caricamento dei tuoi achievement...")}
        </div>
      )}
    </div>
  );
};
